/* =============================================================================
   CORE / SERVICE-CHANNELS — Single-Source-of-Truth für Service-Kanäle
   =============================================================================
   Session 59 / Phase 47 (Block B)
   ----------------------------------------------------------------------------
   Hintergrund (Memory `feedback_gg_counter_single_source` analog):
   bis S58 war der Service-Kanal ('dine-in' | 'takeaway') an mindestens 6
   verschiedenen Stellen als String-Literal verstreut:

     - public/adapters/storage.js     (Z. 278, 874, 1204-1207, 1223, 1255)
     - src/views/customer-form.js     (Z. 40, 342, 451, 470, 487-498, 625-629)
     - src/views/takeaway-form.js
     - src/views/admin/admin-order-entry.js
     - SQL CHECK-Constraints (orders + reservations)
     - mehrere Templates / Reports

   Jede Stelle hatte eigene Defaults, eigene Vergleiche, eigene String-Casts.
   Mit Einführung von 'delivery' in Phase 47 wird das unhaltbar — der nächste
   Kanal-Add wäre 6+ Sucheinträge in der Codebase. Dieses Modul ist die
   gemeinsame Quelle der Wahrheit. Wer einen Service-Kanal prüft, importiert
   die Konstante hier statt eines magischen Strings.

   Wenn neue Kanäle (z.B. 'catering', 'event') hinzukommen, gehören sie hier
   rein UND es braucht eine Migration, die den DB-CHECK erweitert. Beide
   Schichten müssen synchron bleiben.
============================================================================ */

// ---------------------------------------------------------------------------
// Kanal-Konstanten — exakt wie sie in der DB stehen (CHECK-Constraint-Werte).
// ---------------------------------------------------------------------------
export const SERVICE_DINE_IN  = 'dine-in';
export const SERVICE_TAKEAWAY = 'takeaway';
export const SERVICE_DELIVERY = 'delivery';

/**
 * Alle gültigen Service-Kanäle. Muss synchron mit dem CHECK-Constraint in
 * orders.service_type + reservations.service_type bleiben (siehe Migration
 * phase_47_service_type_delivery).
 */
export const SERVICE_CHANNELS = Object.freeze([
  SERVICE_DINE_IN,
  SERVICE_TAKEAWAY,
  SERVICE_DELIVERY
]);

const SERVICE_CHANNELS_SET = new Set(SERVICE_CHANNELS);

/**
 * Lesbare deutsche Labels für UI-Anzeige. Caller, die ihre eigenen Labels
 * brauchen, importieren diese und überschreiben pro View.
 */
export const SERVICE_CHANNEL_LABELS = Object.freeze({
  [SERVICE_DINE_IN]:  'Vor Ort',
  [SERVICE_TAKEAWAY]: 'Abholung',
  [SERVICE_DELIVERY]: 'Lieferung'
});

/**
 * Default-Kanal — fallback wenn nicht explizit gesetzt. Historisch 'dine-in',
 * weil Reservierungen typischerweise Tisch-basiert sind. Bewusst NICHT
 * 'takeaway' oder 'delivery' (dort gibt es immer einen aktiven Submit-Pfad).
 */
export const DEFAULT_SERVICE_CHANNEL = SERVICE_DINE_IN;

// ---------------------------------------------------------------------------
// Helper-Funktionen — Typ-Tests + Sanitizer.
// ---------------------------------------------------------------------------

/** Liest service_type aus snake/camel-Objekten defensiv. */
function _serviceTypeOf(obj) {
  if (!obj) return '';
  return obj.serviceType || obj.service_type || '';
}

/** Akzeptiert {string|object|null} und liefert den raw-String oder ''. */
function _normalizeInput(input) {
  if (typeof input === 'string') return input.trim();
  return _serviceTypeOf(input);
}

/** Ist der gegebene Kanal-Wert gültig (in unserer Enum-Liste)? */
export function isValidServiceChannel(value) {
  return SERVICE_CHANNELS_SET.has(_normalizeInput(value));
}

/** Order/Reservation ist dine-in (Tisch erforderlich). */
export function isDineIn(input) {
  return _normalizeInput(input) === SERVICE_DINE_IN;
}

/** Order/Reservation ist takeaway (Abholung). */
export function isTakeaway(input) {
  return _normalizeInput(input) === SERVICE_TAKEAWAY;
}

/** Order/Reservation ist delivery (Auslieferung an Adresse). */
export function isDelivery(input) {
  return _normalizeInput(input) === SERVICE_DELIVERY;
}

/**
 * Sanitizer für Form-Inputs / API-Inputs. Liefert immer einen gültigen
 * Service-Channel — fallback auf DEFAULT_SERVICE_CHANNEL bei unbekanntem
 * Wert. Erlaubt sowohl String-Input ('takeaway') als auch Objekte
 * (`{serviceType: 'takeaway'}`).
 */
export function coerceServiceChannel(input) {
  const v = _normalizeInput(input);
  return SERVICE_CHANNELS_SET.has(v) ? v : DEFAULT_SERVICE_CHANNEL;
}

/** Lesbares Label für UI-Anzeige. Unknown → '—'. */
export function serviceChannelLabel(input) {
  const v = _normalizeInput(input);
  return SERVICE_CHANNEL_LABELS[v] || '—';
}

/**
 * Liefert true, wenn der Kanal eine physische Übergabe ohne Tisch hat
 * (takeaway + delivery). Dient z.B. Order-Routing-Entscheidungen
 * („Bondrucker auslösen sobald ready?").
 */
export function isOffPremise(input) {
  const v = _normalizeInput(input);
  return v === SERVICE_TAKEAWAY || v === SERVICE_DELIVERY;
}

// ---------------------------------------------------------------------------
// Backward-Compat: window-Zugriff für inline-Handler in Cockpit-UI.
// Analog zu order-statuses.js / order-sources.js.
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  window.ServiceChannels = {
    SERVICE_DINE_IN,
    SERVICE_TAKEAWAY,
    SERVICE_DELIVERY,
    SERVICE_CHANNELS,
    SERVICE_CHANNEL_LABELS,
    DEFAULT_SERVICE_CHANNEL,
    isValidServiceChannel,
    isDineIn,
    isTakeaway,
    isDelivery,
    isOffPremise,
    coerceServiceChannel,
    serviceChannelLabel
  };
}
